import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getItem } from '../api/hn_api';
import LoadOnScroll from './LoadOnScroll';
import StoryBox from './StoryBox';

import {timeAgo, getHostname} from './util'


const CircularProgress = ({ progress }) => {
  progress = Math.min(progress, 100)
  const radius = 32;
  const stroke = 2
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  return (
    <div className='w-[70px] flex-shrink-0 flex-grow-0'>
    <svg viewBox="0 0 100 100">
      {/* Background Circle */}
      <circle cx="50" cy="50" stroke="#948f8c5b"  strokeWidth={stroke+1} fill="none" r={radius} />
      {/* Progress Circle */}
      <circle
        cx="50"
        cy="50"
        r={radius}
        stroke="#e0e0e0"
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform="rotate(-90 50 50)"  // Rotates the progress start to the top
      />

      <svg xmlns="http://www.w3.org/2000/svg" x="30" y="30" width="40" height="40" viewBox="0 0 256 256">
          <rect width="256" height="256" fill="none" />
          <path fill="currentColor" d="M208 34H83.31a13.94 13.94 0 0 0-9.9 4.1L38.1 73.41a13.94 13.94 0 0 0-4.1 9.9V208a14 14 0 0 0 14 14h160a14 14 0 0 0 14-14V48a14 14 0 0 0-14-14M86 46h84v34a2 2 0 0 1-2 2H88a2 2 0 0 1-2-2Zm124 162a2 2 0 0 1-2 2H48a2 2 0 0 1-2-2V83.31a2 2 0 0 1 .59-1.41L74 54.48V80a14 14 0 0 0 14 14h80a14 14 0 0 0 14-14V46h26a2 2 0 0 1 2 2Z" />
          {progress === 100 ? (
            <path fill="none" stroke="currentColor" stroke-width="12" stroke-linecap="round" stroke-linejoin="round" d="M96 145l20 20l44-44" />
          ): <g>
            <circle cx="90" cy="145" r="10" fill="currentColor"/>
            <circle cx="130" cy="145" r="10" fill="currentColor"/>
            <circle cx="170" cy="145" r="10" fill="currentColor"/>
          </g> }
        </svg>
      
    </svg>
    </div>
  );
};



const Comment = ({ commentID, level = 0, comments }) => {
  const [showKids, setShowKids] = useState(true);

  if (!comments) return null;
  const data = comments[commentID]

  const toggleKids = () => {
    setShowKids(!showKids);
  };


  if (!data) {
    return null;
  }

  const numKids = data.kids.length

  return (
    <div className="comment-box" style={{ marginLeft: `${Math.min(level,6) * 4}px` }}>
      <div onClick={toggleKids}>
        <div  className="flex justify-between items-center comment-header">
          <div className='text-[0.8em]'>
            <i className="fa-solid fa-user mr-[10px]"></i> 
            { data.deleted ? <span className='italic'> deleted </span> : <span> {data.by}  </span>}
            <i className="fa-regular fa-clock mb-[-1px] px-[10px]"></i>
            <span className=''>{timeAgo(data.time)}</span>
          </div>
          <div className='text-[0.90em]'>
            {numKids > 0 ? numKids : ''} {showKids ? <i className="fa-regular fa-comment"></i>:<i className="fas fa-comment"></i>}
          </div>
          

        </div>
        {showKids ? (
          <p className='comment-text text-[0.8em]' dangerouslySetInnerHTML={{ __html: data.text || '' }} />
        ) : <div/>}
      </div>
      {showKids && data.kids ? (
        <div>
          {data.kids.map((kidId) => (
            <Comment key={kidId} commentID={kidId} level={level + 1} comments={comments} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const StoryDetails = ({ offlineMode }) => {
  const { id } = useParams();
  const storyID = id;
  const [story, setStory] = useState(null);
  const [ comments, setComments ] = useState({})


  const commentsLoaded = useRef(0)
  let commentsTotal = useRef(100)

  const fetchComment = async (commentID, depth_first=false) => {
    const commentData = await getItem(commentID, offlineMode);
    if (!commentData) return;
    commentData.kids = commentData.kids || [];
    commentsLoaded.current += 1
    setComments(prev => ({...prev, [commentID]: commentData}))

    //at the base of the recursion tree, do checks to see if we've completely cached this page
    if (commentsLoaded.current >= commentsTotal.current) {
      localStorage.setItem(`story-comments-cached-${storyID}`, true);
    }
    for (let kidID of commentData.kids){
      if (depth_first) {
        await fetchComment(kidID, depth_first)
      } else {
        fetchComment(kidID)
      }
    }
  }
  const fetchStory = async () => {
    let storyData 
    try {
      storyData = await getItem(id, offlineMode);
      
      storyData.kids = storyData.kids || [];
      setStory(storyData); 

      commentsTotal.current = storyData.descendants
      commentsLoaded.current = 0

      let topLevelIndex = 0
      while (topLevelIndex < storyData.kids.length && commentsLoaded.current < 10) {
        try { //load depth first until we have at least 10 comments for continuity
          await fetchComment(storyData.kids[topLevelIndex], true);
        } catch (err) {
          console.error("Error fetching comment:", err);
        }
        topLevelIndex += 1
      }
      // parallelize the rest breadth first for speed
      storyData.kids.slice(topLevelIndex).map(kidID => {
        try {
          fetchComment(kidID);
        } catch (err) {
          console.error("Error fetching comment:", err);
        }
        return false
      })
    } catch (err) {
      console.error("Error fetching story details:", err);
    }
  };

  const toggleSaved = () => {

  }

  let progress_pct = Math.round((commentsLoaded.current / commentsTotal.current) * 100);
  progress_pct = offlineMode && (progress_pct > 80) ? 100 : progress_pct
  // const favorited = true
  const rightSide_rendered = (
    <div className='flex pr-3 items-center' onClick={toggleSaved}>
      {/* <div className='w-[30px] flex-shrink-0 mr-[5px]'>
        {favorited ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256">
            <rect width="256" height="256" fill="none" />
            <path fill="currentColor" d="M240 102c0 70-103.79 126.66-108.21 129a8 8 0 0 1-7.58 0C119.79 228.66 16 172 16 102a62.07 62.07 0 0 1 62-62c20.65 0 38.73 8.88 50 23.89C139.27 48.88 157.35 40 178 40a62.07 62.07 0 0 1 62 62" />
          </svg>) : (<svg xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 256 256">
            <rect width="256" height="256" fill="none" />
            <path fill="currentColor" d="M178 40c-20.65 0-38.73 8.88-50 23.89C116.73 48.88 98.65 40 78 40a62.07 62.07 0 0 0-62 62c0 70 103.79 126.66 108.21 129a8 8 0 0 0 7.58 0C136.21 228.66 240 172 240 102a62.07 62.07 0 0 0-62-62m-50 174.8c-18.26-10.64-96-59.11-96-112.8a46.06 46.06 0 0 1 46-46c19.45 0 35.78 10.36 42.6 27a8 8 0 0 0 14.8 0c6.82-16.67 23.15-27 42.6-27a46.06 46.06 0 0 1 46 46c0 53.61-77.76 102.15-96 112.8" />
          </svg>
        )}
      </div> */}
      <CircularProgress progress={progress_pct}  />
    </div>
  )

  return (
    <LoadOnScroll refreshContent={fetchStory} loadMoreContent={null} hideImmediately={true} disabled={offlineMode}>
      <StoryBox story={story} rightSide={rightSide_rendered} />
        {story && story.kids.length > 0 ? story.kids.map((commentID) => (
            <Comment key={commentID} commentID={commentID} level={0} comments={comments} />
          )) : ( 
          null
        )}
    </LoadOnScroll>
  );
};

export default StoryDetails;